"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";
import { refusalProps, withDisabledReason } from "./disabled-reason";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/* The Sirdab segmented control: a radio group wearing a track. This is
   the ANIMATED, icon-led variant: alpha-100 track (h-10, p-1,
   rounded-xl 12px) holding h-8 items one radius step in (rounded-lg,
   10px, the system's nesting step); the checked item
   carries a floating background thumb (bg-background, half opacity in
   dark, raised shadow) that SLIDES between options as a shared layout
   element, and its label EXPANDS into view while unchecked items stay
   icon-only. Icons rise from the muted tone to ink over 200ms.

   The label is the label role (13px/20px) and the thumb wears the
   raised shadow. Roving focus, arrow
   keys, and RTL come from Radix RadioGroup.

   A STATIC text segmented control (gray-75 track, bordered active tab,
   Tabs semantics) also exists in the system. That is a separate Tabs
   variant, not this component.

   Usage:
       <Segmented value={mode} onValueChange={setMode} aria-label="وضع الإنشاء">
           <SegmentedItem value="generate" icon={<Icon icon={AudioLines} />} label="توليد" />
           <SegmentedItem value="video" icon={<Icon icon={Video} />} label="فيديو" />
       </Segmented>
*/

/* Radix doesn't expose the group's value to items; mirror it (plus a
   per-group id so two groups never share a thumb) through context. */
const SegmentedContext = React.createContext<{
  value: string | undefined;
  thumbId: string;
  animated: boolean;
} | null>(null);

function Segmented({
  className,
  value: valueProp,
  defaultValue,
  onValueChange,
  animated = true,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root> & {
  /** false renders the same states with no motion: the thumb appears
        instead of sliding, the label swaps instead of expanding. */
  animated?: boolean;
}) {
  /* Controlled iff the caller passes `value`: mirror state only then
       (the `value ?? internal` shortcut made a controlled group
       un-clearable and flipped an uncontrolled one to controlled after
       the first click; same pattern as FilterChipSelect). */
  const isControlled = valueProp !== undefined;
  const [internal, setInternal] = React.useState(defaultValue);
  // Radix types value as string | null; the context speaks undefined.
  const current = (isControlled ? valueProp : internal) ?? undefined;
  const thumbId = React.useId();
  return (
    <SegmentedContext.Provider value={{ value: current, thumbId, animated }}>
      <RadioGroupPrimitive.Root
        data-slot="segmented"
        className={cn(
          "bg-muted flex h-10 w-fit overflow-hidden rounded-xl p-1",
          className,
        )}
        value={isControlled ? valueProp : undefined}
        defaultValue={defaultValue}
        onValueChange={(next) => {
          if (!isControlled) setInternal(next);
          onValueChange?.(next);
        }}
        {...props}
      />
    </SegmentedContext.Provider>
  );
}

function SegmentedItem({
  className,
  icon,
  label,
  iconOnly,
  hint,
  disabledReason,
  ...props
}: Omit<React.ComponentProps<typeof RadioGroupPrimitive.Item>, "children"> & {
  /** An <Icon> element (a node, not a component; this crosses the
        server boundary). Omit it for a text-only item: its label is then
        ALWAYS visible, which also makes the track width constant, the
        no-layout-shift mode. */
  icon?: React.ReactNode;
  /** With an icon: shown only while checked, expanding in as the thumb
        arrives. Without one (or static): always visible. */
  label: string;
  /** Never render the label visually, checked or not: glyph tabs (a rail
        section switcher). The label still names the radio for AT, and the
        hover name belongs in `hint`. Requires an icon; ignored without one,
        because a control with no glyph and no text is a mystery box. */
  iconOnly?: boolean;
  /** WHY this is unavailable, on a tooltip. Implies disabled, and
        keeps the control reachable so the reason can actually be read
        (see disabled-reason.tsx). */
  disabledReason?: React.ReactNode;
  /** What this MODE does, on a tooltip. A segmented label names the
        option in a word or a glyph; the difference between the options
        usually will not fit there, and this is where it goes. */
  hint?: React.ReactNode;
}) {
  const group = React.useContext(SegmentedContext);
  const checked = group?.value === props.value;
  const glyphOnly = iconOnly === true && icon != null;
  const alwaysLabel = !glyphOnly && (group?.animated === false || icon == null);
  const item = (
    <RadioGroupPrimitive.Item
      data-slot="segmented-item"
      className={cn(
        /* Keyboard focus is the global focus-visible outline.
                   Nothing here sets outline-none (house doctrine, same as
                   Button/Checkbox/Switch). */
        "group relative h-8 rounded-lg px-2",
        // The tighter checked start belongs to the ANIMATED
        // icon+label pair (and animates with it). Static mode
        // drops it entirely: a padding that changes with checked
        // state translates the content 2px on every selection.
        icon != null &&
          !glyphOnly &&
          group?.animated !== false &&
          "transition-all duration-200 data-[state=checked]:ps-1.5",
        className,
      )}
      {...props}
      {...refusalProps(disabledReason)}
    >
      {checked ? (
        group?.animated === false ? (
          <span
            data-slot="segmented-thumb"
            className="shadow-raised absolute inset-0 rounded-lg bg-background dark:bg-background/50"
          />
        ) : (
          <motion.span
            layoutId={group?.thumbId}
            data-slot="segmented-thumb"
            // The floating checked surface: slides between options.
            className="shadow-raised absolute inset-0 rounded-lg bg-background dark:bg-background/50"
            transition={{ duration: 0.2 }}
          />
        )
      ) : null}
      <div className="z-raised relative flex h-full items-center justify-center">
        {icon != null ? (
          <span
            className={cn(
              "text-muted-foreground flex shrink-0 items-center group-data-[state=checked]:text-foreground [&_svg:not([class*='size-'])]:size-4",
              group?.animated !== false &&
                "[&_svg]:transition-colors [&_svg]:duration-200",
            )}
          >
            {icon}
          </span>
        ) : null}
        {alwaysLabel ? null : (
          /* The animated variant hides unchecked labels
                       VISUALLY, but a radio's accessible name comes from
                       its content, so the label always exists for AT,
                       and the animated visible copy below is aria-hidden
                       to avoid double-naming. Glyph tabs ride the same
                       span: it is their only name. */
          <span className="sr-only">{label}</span>
        )}
        {glyphOnly ? null : alwaysLabel ? (
          /* Static and text-only show every label: hiding them
                       is only worth it as the expand animation's payoff,
                       and always-visible labels keep the track width
                       constant. */
          <div
            /* Named so a caller can collapse these labels at a
                           breakpoint without rendering a second copy of the
                           whole control. Collapse it with sr-only and never
                           display:none: in this branch the visible label IS the
                           radio's accessible name, so hiding it outright leaves
                           an unnamed control behind. */
            data-slot="segmented-label"
            className={cn(
              "text-label overflow-hidden whitespace-nowrap",
              group?.animated !== false && "transition-colors duration-200",
              checked ? "text-foreground" : "text-muted-foreground",
            )}
          >
            <span className={cn(icon != null && "ms-2")}>{label}</span>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {checked ? (
              <motion.div
                aria-hidden
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="text-label overflow-hidden whitespace-nowrap text-foreground"
              >
                <span className="ms-2">{label}</span>
              </motion.div>
            ) : null}
          </AnimatePresence>
        )}
      </div>
    </RadioGroupPrimitive.Item>
  );

  // A refusal outranks a hint: if the mode cannot be picked, why not
  // is the only thing worth saying.
  if (disabledReason != null) return withDisabledReason(item, disabledReason);
  if (hint == null) return item;
  // The tooltip trigger goes on a WRAPPER, never on the item itself:
  // asChild would merge Radix Tooltip's own data-state (open/closed)
  // onto the radio and overwrite data-state="checked", which is what
  // every checked style here selects on. The wrapper is display
  // contents-free (inline-flex) so the track's flex layout is
  // unchanged, and focus events bubble, so keyboard focus on the radio
  // still opens the hint.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{item}</span>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

export { Segmented, SegmentedItem };
